'use client';

import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';

// ─── TYPES ────────────────────────────────────────────────────────────────────

export type MizanState = 'balanced' | 'recognition-heavy' | 'action-heavy';

export interface MizanData {
  recognitionCount: number;
  actionCount: number;
  ratio: number;
  state: MizanState;
}

// ─── MIZAN CALCULATION ────────────────────────────────────────────────────────

export function computeMizanState(recognitionCount: number, actionCount: number): MizanData {
  const total = recognitionCount + actionCount;
  if (total === 0) return { recognitionCount, actionCount, ratio: 0.5, state: 'balanced' };
  const ratio = recognitionCount / total;
  let state: MizanState = 'balanced';
  if (ratio > 0.6) state = 'recognition-heavy';
  else if (ratio < 0.4) state = 'action-heavy';
  return { recognitionCount, actionCount, ratio, state };
}

// ─── MIZAN INDICATOR ──────────────────────────────────────────────────────────

interface MizanIndicatorProps {
  mizan: MizanData;
  mostRecentEncounter?: string;
  mostRecentResource?: string;
  resourcesVerifiedThisWeek?: number;
}

export function MizanIndicator({
  mizan,
  mostRecentEncounter,
  mostRecentResource,
  resourcesVerifiedThisWeek = 0,
}: MizanIndicatorProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  const tiltDeg = useMemo(() => {
    if (mizan.state === 'balanced') return 0;
    if (mizan.state === 'recognition-heavy') return -18;
    return 18;
  }, [mizan.state]);

  const scaleColor = useMemo(() => {
    if (mizan.state === 'balanced') return '#c9a84c';
    if (mizan.state === 'recognition-heavy') return '#2d8a87';
    return '#2d7a47';
  }, [mizan.state]);

  const stateLabel = useMemo(() => {
    if (mizan.state === 'balanced') return 'في الميزان — In the balance';
    if (mizan.state === 'recognition-heavy') return 'Recognition outpaces action';
    return 'Action outpaces recognition';
  }, [mizan.state]);

  return (
    <div style={{ position: 'relative' }}>
      <div
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          cursor: 'pointer',
          padding: '8px 12px',
          borderRadius: 10,
          background: 'rgba(255,255,255,0.02)',
          border: `1px solid ${scaleColor}25`,
          transition: 'all 0.3s ease',
          minWidth: 120,
        }}
      >
        {/* Scale icon */}
        <div style={{ position: 'relative', width: 48, height: 32 }}>
          {/* Beam */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 40,
              height: 2,
              background: scaleColor,
              transformOrigin: 'center center',
              transform: `translate(-50%, -50%) rotate(${tiltDeg}deg)`,
              transition: 'transform 0.6s ease, background 0.3s ease',
              borderRadius: 2,
            }}
          />
          {/* Center pivot */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: scaleColor,
              transform: 'translate(-50%, -50%)',
              transition: 'background 0.3s ease',
            }}
          />
          {/* Left pan */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: `calc(50% + ${tiltDeg > 0 ? 8 : -8}px)`,
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: `${scaleColor}40`,
              border: `1px solid ${scaleColor}60`,
              transform: 'translateY(-50%)',
              transition: 'all 0.6s ease',
            }}
          />
          {/* Right pan */}
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: `calc(50% + ${tiltDeg < 0 ? 8 : -8}px)`,
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: `${scaleColor}40`,
              border: `1px solid ${scaleColor}60`,
              transform: 'translateY(-50%)',
              transition: 'all 0.6s ease',
            }}
          />
        </div>

        {/* Labels */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#2d8a87' }}>
            إِدْرَاك
          </span>
          <span style={{ color: 'rgba(212,197,169,0.2)', fontSize: 8 }}>·</span>
          <span style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#2d7a47' }}>
            عَمَل
          </span>
        </div>

        {/* State label */}
        <p style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '0.06em', color: `${scaleColor}80`, textAlign: 'center', lineHeight: 1.3 }}>
          {stateLabel}
        </p>
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: 8,
            zIndex: 500,
            width: 280,
            background: '#0a1218',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 10,
            padding: '14px 16px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#2d8a87', marginBottom: 2 }}>إِدْرَاك Recognition</p>
              <p style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 'bold', color: '#2d8a87' }}>{mizan.recognitionCount}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#2d7a47', marginBottom: 2 }}>عَمَل Action</p>
              <p style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 'bold', color: '#2d7a47' }}>{mizan.actionCount}</p>
            </div>
          </div>

          {mizan.state === 'recognition-heavy' && mostRecentEncounter && (
            <div style={{ borderTop: '1px solid rgba(201,168,76,0.1)', paddingTop: 10 }}>
              <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 10, lineHeight: 1.6, marginBottom: 8 }}>
                Your garden is rich with recognition. The Amanah of action awaits.
              </p>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10, lineHeight: 1.5 }}>
                Your observation of <em>{mostRecentEncounter}</em> identified a Name. Bridge this Amanah?
              </p>
              <Link
                href="/muslim-resource-guide"
                style={{ display: 'block', marginTop: 8, fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#2d7a47' }}
              >
                Bridge this Amanah →
              </Link>
            </div>
          )}

          {mizan.state === 'action-heavy' && (
            <div style={{ borderTop: '1px solid rgba(201,168,76,0.1)', paddingTop: 10 }}>
              <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 10, lineHeight: 1.6, marginBottom: 8 }}>
                Your actions run ahead of recognition. Pause. What Names compose the work you&apos;re doing?
              </p>
              {resourcesVerifiedThisWeek > 0 && (
                <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10, lineHeight: 1.5, marginBottom: 8 }}>
                  You verified {resourcesVerifiedThisWeek} resource{resourcesVerifiedThisWeek > 1 ? 's' : ''} this week. Apply the Three Questions to one of them.
                </p>
              )}
              <Link
                href="/inner-reality-lens"
                style={{ display: 'block', fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#2d8a87' }}
              >
                Open IRL Lens →
              </Link>
            </div>
          )}

          {mizan.state === 'balanced' && (
            <div style={{ borderTop: '1px solid rgba(201,168,76,0.1)', paddingTop: 10 }}>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 10, lineHeight: 1.6 }}>
                Recognition and action are in balance. The garden flourishes.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MIZAN FRAMEWORK MAP OVERLAY ─────────────────────────────────────────────

interface MizanFrameworkMapProps {
  active: boolean;
}

export function MizanFrameworkMapToggle({ active }: MizanFrameworkMapProps) {
  if (!active) return null;

  const nodes = [
    { id: 'irl', label: 'IRL Lens', type: 'recognition', x: 60, y: 30, color: '#2d8a87' },
    { id: 'names', label: 'Names', type: 'recognition', x: 25, y: 55, color: '#2d8a87' },
    { id: 'keys', label: 'Keys', type: 'balance', x: 50, y: 65, color: '#c9a84c' },
    { id: 'glossary', label: 'Glossary', type: 'balance', x: 75, y: 55, color: '#c9a84c' },
    { id: 'mrg', label: 'MRG', type: 'action', x: 40, y: 80, color: '#2d7a47' },
    { id: 'quests', label: 'Quests', type: 'action', x: 65, y: 80, color: '#2d7a47' },
  ];

  const typeLabels: Record<string, string> = {
    recognition: 'إِدْرَاك Recognition',
    action: 'عَمَل Action',
    balance: 'مِيزَان Balance',
  };

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10 }}>
      {nodes.map(node => (
        <div
          key={node.id}
          style={{
            position: 'absolute',
            left: `${node.x}%`,
            top: `${node.y}%`,
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: `${node.color}30`,
              border: `2px solid ${node.color}`,
              boxShadow: `0 0 8px ${node.color}40`,
            }}
          />
          <span style={{ fontFamily: 'monospace', fontSize: 7, color: node.color, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            {node.label}
          </span>
        </div>
      ))}
      {/* Legend */}
      <div style={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {Object.entries(typeLabels).map(([type, label]) => {
          const color = type === 'recognition' ? '#2d8a87' : type === 'action' ? '#2d7a47' : '#c9a84c';
          return (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
              <span style={{ fontFamily: 'monospace', fontSize: 7, color: `${color}80`, letterSpacing: '0.06em' }}>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── SYSTEMIC MIZAN PANEL (for MRG ward disparity) ───────────────────────────

interface SystemicMizanPanelProps {
  indicatorType: string;
  value: number;
  wardName: string;
}

const SYSTEMIC_MIZAN_DATA: Record<string, { system: string; namesTransgressed: string[]; islahRequired: string }> = {
  'food_desert': {
    system: 'Grocery redlining and disinvestment since the 1960s — deliberate withdrawal of food infrastructure from Black and low-income communities',
    namesTransgressed: ['Ar-Razzaq — provision withdrawn', 'Al-Adl — justice violated'],
    islahRequired: 'Resource connection, community food systems, advocacy for grocery access, urban agriculture',
  },
  'poverty': {
    system: 'Structural disinvestment, wage suppression, and the historical extraction of wealth from communities of color',
    namesTransgressed: ['Ar-Razzaq — provision disrupted', 'Al-Muhaymin — protection withdrawn'],
    islahRequired: 'Economic empowerment programs, job training, financial literacy, community wealth-building',
  },
  'vacancy': {
    system: 'Speculative real estate, foreclosure crises, and deliberate abandonment of urban neighborhoods',
    namesTransgressed: ['Al-Hafiz — preservation violated', 'Al-Muhyi — life withheld from space'],
    islahRequired: 'Community land trusts, housing rehabilitation, anti-speculation policy, community ownership',
  },
  'uninsured': {
    system: 'Systemic exclusion from healthcare access through cost barriers, insurance gaps, and historical medical racism',
    namesTransgressed: ['As-Salam — wholeness denied', 'Ar-Rahman — mercy withheld through system'],
    islahRequired: 'Community health clinics, insurance navigation, mental health access, preventive care programs',
  },
  'default': {
    system: 'Structural systems of disinvestment and historical injustice that have disrupted the original balance',
    namesTransgressed: ['Al-Adl — justice violated', 'Al-Mizan — balance disrupted'],
    islahRequired: 'Community organizing, resource connection, advocacy, and caretaking action',
  },
};

export function SystemicMizanPanel({ indicatorType, value, wardName }: SystemicMizanPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const data = SYSTEMIC_MIZAN_DATA[indicatorType] || SYSTEMIC_MIZAN_DATA.default;

  return (
    <div
      style={{
        marginTop: 12,
        borderRadius: 10,
        background: 'rgba(201,168,76,0.03)',
        border: '1px solid rgba(201,168,76,0.12)',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)' }}>
            مِيزَان — Systemic Imbalance
          </span>
          <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 'bold', color: '#c9a84c' }}>{value}%</span>
        </div>
        <span style={{ color: 'rgba(201,168,76,0.4)', fontSize: 12 }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div style={{ padding: '0 14px 14px' }}>
          <div style={{ marginBottom: 10 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)', marginBottom: 4 }}>
              This imbalance is a product of:
            </p>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6 }}>{data.system}</p>
          </div>
          <div style={{ marginBottom: 10 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)', marginBottom: 4 }}>
              Names transgressed:
            </p>
            {data.namesTransgressed.map(n => (
              <p key={n} style={{ color: '#c9a84c', fontSize: 11, lineHeight: 1.6 }}>· {n}</p>
            ))}
          </div>
          <div style={{ marginBottom: 12 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)', marginBottom: 4 }}>
              Islah required:
            </p>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6 }}>{data.islahRequired}</p>
          </div>
          <Link
            href="/muslim-resource-guide"
            style={{
              display: 'inline-block',
              fontFamily: 'monospace',
              fontSize: 9,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              padding: '8px 16px',
              borderRadius: 6,
              background: 'rgba(45,122,71,0.1)',
              border: '1px solid rgba(45,122,71,0.3)',
              color: '#2d7a47',
              textDecoration: 'none',
            }}
          >
            Bridge this Amanah →
          </Link>
        </div>
      )}
    </div>
  );
}

// ─── GARDEN VISUAL STATE (Mizan-driven) ──────────────────────────────────────

export function getMizanGardenStyle(state: MizanState): React.CSSProperties {
  if (state === 'balanced') return {};
  if (state === 'recognition-heavy') {
    return {
      filter: 'saturate(1.15) brightness(1.05)',
    };
  }
  // action-heavy
  return {
    filter: 'saturate(0.85) brightness(0.95)',
  };
}
